import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Car, AlertCircle, RefreshCw, FileText, Download, Link as LinkIcon, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { API } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";

// Helper function to extract error message from API response
const getErrorMessage = (err, defaultMsg = "An error occurred") => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(e => {
      const fieldPath = e.loc ? e.loc.filter(l => l !== 'body').join('.') : '';
      const fieldName = fieldPath || 'Unknown field';
      const message = e.msg || e.message || 'Invalid value';
      return `${fieldName}: ${message}`;
    }).join('. ');
  } else if (typeof detail === 'string') {
    return detail;
  } else if (detail) {
    return JSON.stringify(detail);
  }
  return err?.message || defaultMsg;
};

// ============== INSPECTION PERMANENT VIEW (GH STORAGE FLOW) ==============
const InspectionPermanentView = () => {
  const { inspectionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInspection = async () => {
      try {
        const res = await axios.get(`${API}/i/${inspectionId}`);
        setData(res.data);
      } catch (err) {
        setError(getErrorMessage(err, "Failed to load inspection"));
      } finally {
        setLoading(false);
      }
    };
    fetchInspection();
  }, [inspectionId]);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(`${label} copied to clipboard!`);
    }
  };

  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/i/${inspectionId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const reg = (data?.job?.reg || data?.inspection?.reg || 'report').replace(/\s+/g, '');
      const dd = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const dateStr = `${pad(dd.getDate())}${pad(dd.getMonth() + 1)}${String(dd.getFullYear()).slice(-2)}`;
      link.setAttribute('download', `${reg}_Safety_Inspection_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to download VI PDF"));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "red": return "bg-red-500";
      case "amber": return "bg-amber-500";
      case "green": return "bg-green-500";
      case "blue": return "bg-blue-500";
      case "dangerous": return "bg-black";
      default: return "bg-gray-300";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "red": return <Badge className="bg-red-500">Safety Critical</Badge>;
      case "amber": return <Badge className="bg-amber-500">Advisory</Badge>;
      case "green": return <Badge className="bg-green-500">Passed</Badge>;
      case "blue": return <Badge className="bg-blue-500">N/A</Badge>;
      case "dangerous": return <Badge className="bg-black text-white border-2 border-red-600">⚠️ DANGEROUS</Badge>;
      default: return <Badge variant="outline">Not Inspected</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-slate-400" />
          <p className="text-slate-500">Loading inspection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="p-4 max-w-xl mx-auto">
          <Card className="border-red-200">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-red-800">Access Denied</h2>
              <p className="text-red-600 mt-2">{error}</p>
              <Button className="mt-4" onClick={() => navigate("/app/foh")}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { inspection, job, items, arrival_photos, status_counts, creator_name, permanent_link, gh_note_text } = data;

  // Group items by section
  const sectionNames = {
    in_car_checks: "In-Car Checks",
    under_bonnet: "Under-Bonnet",
    brakes_wheels: "Brakes & Wheels",
    tyres: "Tyres",
    suspension_steering: "Suspension & Steering",
    exhaust_underside: "Exhaust & Underside",
    lights_electrical: "Lights & Electrical",
    final_checks: "Final Checks"
  };

  const itemsBySection = {};
  items.forEach(item => {
    if (!itemsBySection[item.section]) {
      itemsBySection[item.section] = [];
    }
    itemsBySection[item.section].push(item);
  });

  // Filter items by status for summary
  const redItems = items.filter(i => i.status === "red");
  const amberItems = items.filter(i => i.status === "amber");

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={selectedPhoto} 
              alt="Evidence" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="p-4 max-w-4xl mx-auto">
        {/* Header Card */}
        <Card className="mb-4" data-testid="inspection-view-card">
          <CardHeader className="bg-slate-50 border-b">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Car className="w-6 h-6" />
                  {job.reg}
                </CardTitle>
                <CardDescription className="text-lg">
                  {job.make} {job.model} {job.year && `(${job.year})`}
                </CardDescription>
              </div>
              <Badge 
                variant={inspection.status === "submitted" ? "default" : "secondary"}
                className={inspection.status === "submitted" ? "bg-green-600" : ""}
              >
                {inspection.status === "submitted" ? "Completed" : "In Progress"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              <div className="text-center p-2 bg-black rounded">
                <p className="text-2xl font-bold text-white">{status_counts.dangerous || 0}</p>
                <p className="text-xs text-gray-300">Dangerous</p>
              </div>
              <div className="text-center p-2 bg-red-50 rounded">
                <p className="text-2xl font-bold text-red-600">{status_counts.red}</p>
                <p className="text-xs text-red-600">Red</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded">
                <p className="text-2xl font-bold text-amber-600">{status_counts.amber}</p>
                <p className="text-xs text-amber-600">Amber</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <p className="text-2xl font-bold text-green-600">{status_counts.green}</p>
                <p className="text-xs text-green-600">Green</p>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded">
                <p className="text-2xl font-bold text-blue-600">{status_counts.blue}</p>
                <p className="text-xs text-blue-600">N/A</p>
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
              <p><strong>Mileage:</strong> {inspection.mileage?.toLocaleString()} miles</p>
              <p><strong>Inspected by:</strong> {creator_name}</p>
              {job.gh_job_ref && <p><strong>GH Job Ref:</strong> {job.gh_job_ref}</p>}
              {inspection.started_at && (
                <p><strong>Date:</strong> {new Date(inspection.started_at).toLocaleDateString('en-GB')}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Copy/Download Actions */}
        <Card className="mb-4">
          <CardHeader className="py-3 px-4 bg-slate-50 border-b">
            <CardTitle className="text-sm">Garage Hive Integration</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {/* Copy Link */}
            <div className="flex gap-2">
              <Input 
                value={permanent_link} 
                readOnly 
                className="bg-white text-sm font-mono flex-1"
                data-testid="permanent-link-display"
              />
              <Button 
                onClick={() => copyToClipboard(permanent_link, "Inspection link")}
                size="sm"
                data-testid="copy-link-btn"
              >
                <LinkIcon className="w-4 h-4 mr-1" />
                Copy Link
              </Button>
            </div>

            {/* Copy GH Note */}
            <div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                <pre className="text-xs whitespace-pre-wrap font-mono text-amber-900">
                  {gh_note_text}
                </pre>
              </div>
              <Button 
                onClick={() => copyToClipboard(gh_note_text, "GH note")}
                variant="outline"
                size="sm"
                className="w-full"
                data-testid="copy-gh-note-btn"
              >
                <FileText className="w-4 h-4 mr-1" />
                Copy Garage Hive Note
              </Button>
            </div>

            {/* Download PDF */}
            <Button 
              onClick={downloadPdf}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Download PDF Report
            </Button>
          </CardContent>
        </Card>

        {/* Arrival Photos */}
        {arrival_photos && arrival_photos.length > 0 && (
          <Card className="mb-4">
            <CardHeader className="py-3 px-4 bg-slate-50 border-b">
              <CardTitle className="text-sm">Arrival Photos</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-4 gap-2">
                {arrival_photos.map((photo, idx) => (
                  <div 
                    key={idx} 
                    className="aspect-square rounded overflow-hidden cursor-pointer hover:opacity-80"
                    onClick={() => setSelectedPhoto(photo.photo_url)}
                  >
                    <img 
                      src={photo.photo_url} 
                      alt={`${photo.corner}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Red Items */}
        {redItems.length > 0 && (
          <Card className="mb-4 border-red-200">
            <CardHeader className="py-3 px-4 bg-red-50 border-b border-red-200">
              <CardTitle className="text-sm text-red-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Safety Critical Items ({redItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {redItems.map((item, idx) => (
                <div key={item.id} className={`p-3 ${idx < redItems.length - 1 ? 'border-b' : ''}`}>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500 mt-1 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.item_label}</p>
                      {item.notes && (
                        <p className="text-sm text-slate-600 mt-1">{item.notes}</p>
                      )}
                      {item.measurement_value && (
                        <p className="text-xs text-slate-500 mt-1">
                          Measurement: {item.measurement_value} {item.measurement_unit}
                        </p>
                      )}
                      {/* Photos */}
                      {(item.photo_urls?.length > 0 || item.tester_photo_url) && (
                        <div className="flex gap-2 mt-2">
                          {item.photo_urls?.map((url, pIdx) => (
                            <div 
                              key={pIdx}
                              className="w-16 h-16 rounded overflow-hidden cursor-pointer"
                              onClick={() => setSelectedPhoto(url)}
                            >
                              <img src={url} alt="Evidence" className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {item.tester_photo_url && (
                            <div 
                              className="w-16 h-16 rounded overflow-hidden cursor-pointer border-2 border-blue-400"
                              onClick={() => setSelectedPhoto(item.tester_photo_url)}
                            >
                              <img src={item.tester_photo_url} alt="Tester" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Amber Items */}
        {amberItems.length > 0 && (
          <Card className="mb-4 border-amber-200">
            <CardHeader className="py-3 px-4 bg-amber-50 border-b border-amber-200">
              <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Advisory Items ({amberItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {amberItems.map((item, idx) => (
                <div key={item.id} className={`p-3 ${idx < amberItems.length - 1 ? 'border-b' : ''}`}>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500 mt-1 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.item_label}</p>
                      {item.notes && (
                        <p className="text-sm text-slate-600 mt-1">{item.notes}</p>
                      )}
                      {item.measurement_value && (
                        <p className="text-xs text-slate-500 mt-1">
                          Measurement: {item.measurement_value} {item.measurement_unit}
                        </p>
                      )}
                      {/* Photos */}
                      {(item.photo_urls?.length > 0 || item.tester_photo_url) && (
                        <div className="flex gap-2 mt-2">
                          {item.photo_urls?.map((url, pIdx) => (
                            <div 
                              key={pIdx}
                              className="w-16 h-16 rounded overflow-hidden cursor-pointer"
                              onClick={() => setSelectedPhoto(url)}
                            >
                              <img src={url} alt="Evidence" className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {item.tester_photo_url && (
                            <div 
                              className="w-16 h-16 rounded overflow-hidden cursor-pointer border-2 border-blue-400"
                              onClick={() => setSelectedPhoto(item.tester_photo_url)}
                            >
                              <img src={item.tester_photo_url} alt="Tester" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* All Sections */}
        <Card className="mb-4">
          <CardHeader className="py-3 px-4 bg-slate-50 border-b">
            <CardTitle className="text-sm">Full Inspection Details</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {Object.entries(sectionNames).map(([sectionId, sectionName]) => {
              const sectionItems = itemsBySection[sectionId] || [];
              if (sectionItems.length === 0) return null;
              
              return (
                <div key={sectionId} className="border-b last:border-b-0">
                  <div className="p-3 bg-slate-50">
                    <h3 className="font-semibold text-sm">{sectionName}</h3>
                  </div>
                  <div className="divide-y">
                    {sectionItems.map(item => (
                      <div key={item.id} className="p-3 flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full shrink-0 ${getStatusColor(item.status)}`} />
                        <span className="text-sm flex-1">{item.item_label}</span>
                        {item.notes && (
                          <span className="text-xs text-slate-500 max-w-[200px] truncate">{item.notes}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="text-center">
          <Button variant="outline" onClick={() => navigate("/app/foh")}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InspectionPermanentView;
